import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import { Input } from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader";
import { getDevice, createDevice, updateDevice } from "./deviceService";
import { deviceForm } from "../configs/Forms/AccountsForms";
import { serverHost } from "variables/serverdata";

const titleCase = (s) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

function getAuthToken() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return localStorage.getItem("token");
    const parsed = JSON.parse(raw);
    return parsed?.access_token || parsed?.token || parsed?.accessToken || null;
  } catch (e) {
    console.error("Failed to parse localStorage user:", e);
    return localStorage.getItem("token");
  }
}

const DeviceAddEdit = () => {
  const { device_uuid } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(device_uuid);

  const [device, setDevice] = useState({});
  const [lines, setLines] = useState([]);
  const [keys, setKeys] = useState([]);
  const [settings, setSettings] = useState([]);
  //const [users, setUsers] = useState([]);
  const [domains, setDomains] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(editing);
  const [error, setError] = useState("");
  const [profiles, setProfiles] = useState([]);

  const setterMap = { Lines: setLines, Keys: setKeys, Settings: setSettings };
  const arrayMap = { Lines: lines, Keys: keys, Settings: settings };

  // Fetch domains
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const res = await fetch(
          `${serverHost}/get_rows?table=v_domains&page=1&pageSize=1000`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (Array.isArray(data.results)) setDomains(data.results);
      } catch (err) {
        console.error("Error fetching domains:", err);
      }
    };
    fetchDomains();
  }, []);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;

        const site = JSON.parse(localStorage.getItem("selectedSite"));
        const domain_uuid = site?.domain_uuid || site?.site_uuid; // whichever you use
        if (!domain_uuid) return;

        const res = await fetch(
          `${serverHost}/get_rows?table=v_device_profiles&domain_uuid=${domain_uuid}&page=1&pageSize=1000`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (Array.isArray(data.results)) setProfiles(data.results);
      } catch (err) {
        console.error("Error fetching device profiles:", err);
      }
    };

    fetchProfiles();
  }, []);

  // Fetch users
  /*   useEffect(() => {
      const fetchUsers = async () => {
        try {
          const site_uuid =
            JSON.parse(localStorage.getItem("selectedSite"))?.site_uuid || null;
  
          const token = getAuthToken();
          if (!token) return;
          const res = await fetch(`${serverHost}/get_users_by_site/${site_uuid}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await res.json();
          if (data.message === "success") setUsers(data.data);
        } catch (err) {
          console.error("Error fetching users:", err);
        }
      };
      fetchUsers();
    }, []); */

  // Fetch vendors
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const res = await fetch(
          `${serverHost}/get_rows?table=v_device_vendors&page=1&pageSize=1000`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (Array.isArray(data.results)) setVendors(data.results);
      } catch (err) {
        console.error("Error fetching domains:", err);
      }
    };
    fetchVendors();
  }, []);

  // Fetch device if editing
  useEffect(() => {
    if (!editing) return;
    const fetchDevice = async () => {
      try {
        setLoading(true);
        const data = await getDevice(device_uuid);
        if (data.error) {
          setError(data.error);
          return;
        }
        setDevice({ ...data, user_id: data.device_user_uuid || "" });
        setLines(data.lines || []);
        setKeys(data.keys || []);
        setSettings(data.settings || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchDevice();
  }, [device_uuid, editing]);

  // Helper functions
  const handleChange = (field, value) => setDevice(prev => ({ ...prev, [field]: value }));
  const handleArrayChange = (setter, index, field, value, array) => {
    const updated = [...array];
    updated[index] = { ...updated[index], [field]: value };
    setter(updated);
  };
  const handleAddItem = (setter, emptyTemplate, array) => setter([...array, { ...emptyTemplate }]);

  // Delete item (local if new, backend if existing)
  const handleDeleteItem = async (section, item) => {
    const token = getAuthToken();
    if (!token) return;

    const sectionMap = {
      Lines: { type: "line", uuidField: "device_line_uuid" },
      Keys: { type: "key", uuidField: "device_key_uuid" },
      Settings: { type: "setting", uuidField: "device_setting_uuid" },
    };

    const { type, uuidField } = sectionMap[section];
    const itemUuid = item[uuidField];

    const setter = setterMap[section];
    const array = arrayMap[section];

    // If new item (no UUID), remove locally only
    if (!itemUuid) {
      setter(array.filter(i => i !== item));
      return;
    }

    try {
      const res = await fetch(`${serverHost}/delete_device_item/${type}/${itemUuid}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      if (res.ok) {
        // Remove the deleted item from local state
        setter(array.filter(i => i[uuidField] !== itemUuid));
      } else {
        alert(`Failed to delete: ${data.error}`);
      }
    } catch (err) {
      console.error(err);
      alert(`Error deleting ${type}`);
    }
  };

  const handleSave = async () => {
    setError("");
    const missing = [];
    const user = JSON.parse(localStorage.getItem("user"));
    const user_uuid = user?.user_uuid || null;
    const domain = JSON.parse(localStorage.getItem("selectedSite"));
    const domain_uuid = domain?.site_uuid || null;

    deviceForm.required.forEach(field => {
      if (!device[field]) missing.push(field);
    });

    Object.entries(deviceForm.requiredSections || {}).forEach(([section, fields]) => {
      const items = { Lines: lines, Keys: keys, Settings: settings }[section] || [];
      items.forEach((item, idx) => {
        fields.forEach(f => {
          if (!item[f]) missing.push(`${section}[${idx}].${f}`);
        });
      });
    });

    if (missing.length) {
      setError(`Required fields missing: ${missing.join(", ")}`);
      return;
    }

    try {
      const { user_id, ...restDevice } = device;
      const payload = {
        device: { ...restDevice, device_user_uuid: null, domain_uuid, user_uuid },
        lines,
        keys,
        settings,
      };
      console.log(editing ? "Updating device" : "Creating device", payload);
      if (editing) await updateDevice(device.device_uuid, payload);
      else await createDevice(payload);

      navigate("/admin/hucs/devices");
    } catch (err) {
      setError(err.message || "Failed to save device");
    }
  };

  const renderField = (section, field, value, onChange, dynamicOptions = {}) => {
    const type = deviceForm.types[field];
    const defaultValue = deviceForm.defaults?.[field];
    const effectiveValue = value ?? defaultValue ?? "";
    const label =
      deviceForm.labels?.[field] || titleCase(field);

    const placeholder = value
      ? ""
      : `Enter ${label.toLowerCase()}`;

    // BOOLEAN
    if (type === "boolean") {
      return (
        <Input
          type="select"
          value={effectiveValue}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="true">True</option>
          <option value="false">False</option>
        </Input>
      );
    }

    // SELECT
    if (type === "select") {
      const staticOptions = deviceForm.options?.[field] || [];
      const runtimeOptions = dynamicOptions[field] || [];

      // Normalize everything into a single array
      const mergedOptions = [...staticOptions, ...runtimeOptions];

      return (
        <Input
          type="select"
          value={effectiveValue}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">
            Select {deviceForm.labels[field] || field}
          </option>

          {mergedOptions.map((opt, idx) => {
            // Optgroup support
            if (typeof opt === "object" && opt.options) {
              return (
                <optgroup key={idx} label={opt.label}>
                  {opt.options.map((o) =>
                    typeof o === "object" ? (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ) : (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    )
                  )}
                </optgroup>
              );
            }

            // Object option { label, value }
            if (typeof opt === "object") {
              return (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              );
            }

            // Primitive option
            return (
              <option key={opt} value={opt}>
                {opt}
              </option>
            );
          })}
        </Input>
      );
    }

    // DEFAULT (text / number / etc)
    return (
      <Input
        type="text"
        value={effectiveValue}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  };


  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading device...</p>
      </div>
    );

  const dynamicSelectOptions = {
    domain_uuid: domains.map(d => ({
      label: d.domain_name,
      value: d.domain_uuid,
    })),
    // user_id: users.map(u => ({
    //   label: `${u.first_name} ${u.last_name}`,
    //   value: u.user_uuid,
    // })),
    device_vendor: vendors.map(v => ({
      label: v.name,
      value: v.name,
    })),

    device_profile_uuid: profiles.map(p => ({
      label: p.device_profile_name || p.device_profile_uuid,
      value: p.device_profile_uuid,
    })),
  };

  return (
    <>
      <SimpleHeader name={`${editing ? "Edit" : "Add"} Device`} parentName="Devices" />
      <Container className="mt--6">
        <Card className="shadow-sm" style={{ marginTop: "-3rem" }}>
          <Card.Header className="header-theme">
            <h4 className="mb-0">{editing ? "EDIT DEVICE" : "ADD DEVICE"}</h4>
          </Card.Header>
          <Card.Body className="p-4">
            {error && <Alert variant="danger">{error}</Alert>}
            <Form>
              {Object.entries(deviceForm.sections).map(([sectionName, fields]) => {
                const setter = setterMap[sectionName];
                const items = arrayMap[sectionName];

                return (
                  <div key={sectionName}>
                    <h5 className="section-title mb-3 mt-4" style={{ fontSize: "1.5rem", fontWeight: 700 }}>
                      {sectionName}
                    </h5>

                    {["Lines", "Keys", "Settings"].includes(sectionName) &&
                      items?.map((item, idx) => (
                        <Row key={idx} className="mb-2">
                          {fields.map((field, i) => (
                            <Col md={3} key={i}>
                              <Form.Group className="mb-3">
                                <Form.Label>
                                  {deviceForm.labels[field] || field}
                                  {(deviceForm.requiredSections?.[sectionName] || []).includes(field) && (
                                    <span style={{ color: "red" }}> *</span>
                                  )}
                                </Form.Label>
                                {renderField(sectionName, field, item[field] ?? "", value =>
                                  handleArrayChange(setter, idx, field, value, items)
                                )}
                              </Form.Group>
                            </Col>
                          ))}
                          <Col md={2}>
                            <Button
                              variant="danger"
                              onClick={() => handleDeleteItem(sectionName, item)}
                            >
                              Delete
                            </Button>
                          </Col>
                        </Row>
                      ))}

                    {["Lines", "Keys", "Settings"].includes(sectionName) && (
                      <Button
                        className="mt-2"
                        onClick={() => {
                          const emptyTemplate = {};
                          fields.forEach(f => {
                            emptyTemplate[f] = deviceForm.defaults?.[f] ?? "";
                          });
                          handleAddItem(setter, emptyTemplate, items);
                        }}
                      >
                        Add {sectionName.slice(0, -1)}
                      </Button>
                    )}

                    {sectionName === "Basic Info" && (
                      <Row className="mb-2">
                        {fields.map((field, i) => (
                          <Col md={4} key={i}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                {deviceForm.labels[field] || field}
                                {(deviceForm.required || []).includes(field) && (
                                  <span style={{ color: "red" }}> *</span>
                                )}
                              </Form.Label>

                              {renderField(
                                sectionName,
                                field,
                                device[field] ?? "",
                                value => handleChange(field, value),
                                dynamicSelectOptions
                              )}
                            </Form.Group>
                          </Col>
                        ))}
                      </Row>
                    )}
                  </div>
                );
              })}

              <div className="mt-4 d-flex justify-content-between">
                <Button variant="danger" onClick={() => navigate("/admin/hucs/devices")}>Cancel</Button>
                <Button variant="success" onClick={handleSave}>{"Save"}</Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default DeviceAddEdit;
